/**
 * Authentication context for managing user login state
 * Provides auth state and methods throughout the app
 *
 * Auth token is stored in an HttpOnly cookie on the management API domain.
 * The browser sends it automatically with every request (withCredentials: true).
 * User info is stored in localStorage for UI display purposes.
 *
 * @module contexts/AuthContext
 */

/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAuthData, clearAuthData } from '@/services/authService';
import { checkAuthenticated } from '@/services/httpClient';

interface GitHubUser {
  login: string;
  id: number;
  avatar_url: string;
  name: string;
  email: string;
  authType?: 'github' | 'entra_id';
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: GitHubUser | null;
  login: (user: GitHubUser) => void;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<GitHubUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing auth on mount
  useEffect(() => {
    const initAuth = async () => {
      // Check if management API cookie is still valid
      const hasValidCookie = await checkAuthenticated();

      // Check if we have stored user data
      const authData = getAuthData();
      if (authData && hasValidCookie) {
        // We have both user data and a valid cookie
        const authType =
          authData.user.authType ?? (authData.provider === 'entraid' ? 'entra_id' : 'github');
        const userWithAuthType = { ...authData.user, authType };

        setUser(userWithAuthType);
        setIsAuthenticated(true);
        console.log(
          '[AuthContext] Restored auth session for:',
          authData.user.login,
          'provider:',
          authData.provider
        );
      } else if (authData && !hasValidCookie) {
        // User data exists but cookie expired - clear stale data
        console.log('[AuthContext] User data exists but cookie expired - clearing stale data');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_provider');
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = (newUser: GitHubUser) => {
    setUser(newUser);
    setIsAuthenticated(true);
    console.log('[AuthContext] User logged in:', newUser.login);
  };

  const logout = async () => {
    await clearAuthData();
    setUser(null);
    setIsAuthenticated(false);
    console.log('[AuthContext] User logged out');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
